import React, { ReactElement } from 'react';
import { classNames, shortNumber } from '@fullstory/util/fmt';
import { Box, Button, Flex, SVGTooltip, Text } from '@fullstory/components';
import {
  BOX_LEFT_RIGHT_MARGIN,
  JOINT_WIDTH,
  BOX_WIDTH,
  BOX_HEIGHT,
  SELECTED_NODE_HEIGHT_SCALE,
  SPECIAL_ID_OTHER,
} from './const';
import { GraphNode, PageTypes } from './types';
import css from './index.module.scss';
import { PageTooltipContent } from './TooltipContent';
import { usePageFlow } from './hooks';
import { getPercentString } from '../../util/fmt';
import { int64 } from '@fullstory/proto/google/protobuf';
import { PageNotDefinedInfo } from '@fullstory/library-ui';
import { FormattedMessage } from 'react-intl';

interface PageFlowNodeProps {
  node: GraphNode;
  maxEntryWidth: number;
  maxExitWidth: number;
  name: ReactElement;
  viewMetric?: () => void;
}

export const PageFlowNode: React.FC<PageFlowNodeProps> = ({
  node,
  name,
  maxEntryWidth,
  maxExitWidth,
  viewMetric,
}) => {
  const {
    onWatchSessions,
    isEdit,
    page,
    selectedNode,
    onSelectNode,
    hoveredNode,
    onHoverNode,
    getPageDefTooltip,
    pageDefinition,
  } = usePageFlow();

  const selected = selectedNode?.internalId === node.internalId;
  const hovered = hoveredNode?.internalId === node.internalId;
  // this logic hurt my brain. dim the node when theres *something* selected or hovered but it's not this node.
  const dimmed =
    (!selectedNode && hoveredNode && !hovered) ||
    (selectedNode && !hoveredNode && !selected) ||
    (selectedNode && !selected && !hovered);
  const nodeCount = int64.toNumber(node.count);

  // Can't interact with deleted pages
  const canInteract = !!pageDefinition;

  if (node.type != PageTypes.ENTRY && node.type != PageTypes.EXIT) {
    const entryVisits = int64.toNumber(page.count) - int64.toNumber(page.totalIncomingCount);
    const exitVisits = int64.toNumber(page.count) - int64.toNumber(page.totalOutgoingCount);
    return (
      <>
        {/** The percentage drop in is based on the page views minus the total of the entry pages */}
        {entryVisits > 0 && (
          <SVGTooltip
            variant="dark"
            placement="top"
            tooltipContent={
              <PageTooltipContent
                pageDefId={node.pageDefId}
                name="Entries"
                visits={entryVisits}
                visitsPct={getPercentString(entryVisits, int64.toNumber(page.count))}
                pageType={PageTypes.ENTRY}
              />
            }
          >
            <rect
              id="entryPages"
              x={-BOX_LEFT_RIGHT_MARGIN}
              y={0}
              width={JOINT_WIDTH}
              height={BOX_HEIGHT - maxEntryWidth}
              fill="#6EBB1A"
              fillOpacity={selectedNode ? 0.2 : 1}
            />
          </SVGTooltip>
        )}
        <rect
          x={-BOX_LEFT_RIGHT_MARGIN}
          y={BOX_HEIGHT - maxEntryWidth}
          width={JOINT_WIDTH}
          height={maxEntryWidth}
          className={classNames(css.joint, {
            [css.dimmed]: !!selectedNode || hoveredNode?.type === PageTypes.ENTRY,
          })}
        />
        <rect
          x={BOX_LEFT_RIGHT_MARGIN}
          y={0}
          width={BOX_WIDTH}
          height={BOX_HEIGHT}
          rx="4"
          className={classNames(css.page)}
          style={{ filter: 'url(#pageNodeFilter)' }}
        />
        <foreignObject
          data-component="PageFlowNode"
          x={BOX_LEFT_RIGHT_MARGIN}
          width={BOX_WIDTH}
          height={BOX_HEIGHT}
        >
          <NodeContent
            name={name}
            count={nodeCount}
            percentStr={getPercentString(nodeCount, int64.toNumber(page.count))}
            pageDefId={node.pageDefId}
            pageDefinition={getPageDefTooltip(node.pageDefId)}
          />
        </foreignObject>
        <rect
          x={BOX_WIDTH + BOX_LEFT_RIGHT_MARGIN + BOX_LEFT_RIGHT_MARGIN}
          y={0}
          width={JOINT_WIDTH}
          height={maxExitWidth}
          className={classNames(css.joint, {
            [css.dimmed]: !!selectedNode || hoveredNode?.type === PageTypes.EXIT,
          })}
        />
        {/** The percentage drop off is based on the page views minus the total of the exit pages */}
        {exitVisits > 0 && (
          <SVGTooltip
            variant="dark"
            placement="bottom"
            tooltipContent={
              <PageTooltipContent
                pageDefId={node.pageDefId}
                name="Exits"
                visits={exitVisits}
                visitsPct={getPercentString(exitVisits, int64.toNumber(page.count))}
                pageType={PageTypes.EXIT}
              />
            }
          >
            <rect
              id="exitPages"
              x={BOX_WIDTH + BOX_LEFT_RIGHT_MARGIN + BOX_LEFT_RIGHT_MARGIN}
              y={maxExitWidth}
              width={JOINT_WIDTH}
              height={BOX_HEIGHT - maxExitWidth}
              fill="#F5447B"
              fillOpacity={selectedNode ? 0.2 : 1}
            />
          </SVGTooltip>
        )}
      </>
    );
  }
  const boxHeight =
    BOX_HEIGHT *
    (selected && node?.pageDefId !== SPECIAL_ID_OTHER ? SELECTED_NODE_HEIGHT_SCALE : 1);
  const mouseInteractions = canInteract
    ? {
        onClick: () => onSelectNode(node),
        onMouseEnter: () => onHoverNode(node),
        onMouseLeave: () => onHoverNode(null),
      }
    : {};

  return (
    <g data-component="PageFlowNode" style={{ opacity: dimmed ? 0.6 : 1 }} {...mouseInteractions}>
      <rect
        x={0}
        y={0}
        width={BOX_WIDTH}
        height={boxHeight}
        rx="4"
        className={classNames(css.page, { [css.selected]: selected })}
        style={{ filter: 'url(#pageNodeFilter)' }}
      />
      <foreignObject
        width={BOX_WIDTH}
        height={boxHeight}
        className={classNames({ [css.selectable]: !isEdit && canInteract })}
      >
        <NodeContent
          name={name}
          count={nodeCount}
          percentStr={getPercentString(nodeCount, int64.toNumber(page.count))}
          pageDefId={node.pageDefId}
          pageDefinition={getPageDefTooltip(node.pageDefId)}
          onWatchSessions={
            selected && !isEdit && canInteract && node?.pageDefId !== SPECIAL_ID_OTHER
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onWatchSessions(node.pageDefId, node.type);
                }
              : undefined
          }
          viewMetric={viewMetric}
        />
      </foreignObject>
    </g>
  );
};

interface NodeContentProps {
  name: ReactElement;
  count: number;
  percentStr: string;
  pageDefId?: number;
  pageDefinition?: React.ReactNode;
  onWatchSessions?: (e: React.MouseEvent) => void;
  viewMetric?: () => void;
}

function NodeContent({
  name,
  count,
  percentStr,
  pageDefId,
  pageDefinition,
  onWatchSessions,
  viewMetric,
}: NodeContentProps) {
  return (
    <>
      <Box padding={[8, 12, 0, 12]} style={{ whiteSpace: 'nowrap' }} className="fs-unmask">
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Box className={css.nodeTitle}>{name}</Box>
          {pageDefId === 0 && <PageNotDefinedInfo viewMetric={viewMetric} />}
          {!!pageDefId && !!pageDefinition && pageDefinition}
        </Flex>
      </Box>
      <Box margin={[4, 0, 0, 12]} className="fs-mask">
        <Flex justifyContent="flex-start">
          <Text appearance="bodySmall" color="default">
            {percentStr}
          </Text>
          <Box margin={[0, 4, 0, 4]}>
            <Text appearance="bodySmall" color="default">
              <FormattedMessage
                defaultMessage="·"
                id="G7uas+"
                description="Used as a separator e.g 10% · 100 views"
              />
            </Text>
          </Box>
          <Text appearance="bodySmall" color="default">
            {shortNumber(count)}
          </Text>
        </Flex>
      </Box>
      {onWatchSessions && (
        <Box margin={8}>
          <Button
            className={css.unsetPositionForSafari}
            variant="fill-secondary"
            iconLeft="play"
            size="small"
            onClick={onWatchSessions}
          >
            <FormattedMessage defaultMessage="Watch Sessions" id="4GDhCd" />
          </Button>
        </Box>
      )}
    </>
  );
}
